package ru.chm.gradereport.ui.screens.course

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.model.AttendanceSessionItem
import ru.chm.gradereport.model.HomeworkScoreItem
import ru.chm.gradereport.model.StudentStatsDto
import ru.chm.gradereport.platform.openUrl
import ru.chm.gradereport.ui.components.GradientStatCard
import ru.chm.gradereport.ui.components.LinkedText
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.formatDisplayDate
import ru.chm.gradereport.ui.components.softClickable
import ru.chm.gradereport.ui.screens.course.sheet.SessionAccent
import ru.chm.gradereport.ui.screens.course.sheet.statusColor
import ru.chm.gradereport.ui.screens.course.sheet.statusFullLabel
import ru.chm.gradereport.ui.theme.BlueAccent
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.GreenAccent
import ru.chm.gradereport.ui.theme.YellowAccent
import kotlin.math.roundToInt

private data class SessionStatsGroup(
    val session: AttendanceSessionItem?,
    val homeworks: List<HomeworkScoreItem>,
)

@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun StudentStatsContent(
    stats: StudentStatsDto,
    showSubmissionLinks: Boolean = false,
    onOpenHomework: ((HomeworkScoreItem) -> Unit)? = null,
) {
    val colors = GradeTheme.colors
    val groups = remember(stats.attendanceItems, stats.homeworkScores) {
        buildSessionGroups(stats.attendanceItems, stats.homeworkScores)
    }

    FlowRow(
        modifier = Modifier.padding(top = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        GradientStatCard(
            title = "Посещаемость",
            value = "${stats.attendancePercent.roundToInt()}%",
            subtitle = "П:${stats.attendancePresent} Н:${stats.attendanceAbsent} О:${stats.attendanceLate} У:${stats.attendanceExcused}",
            colors = listOf(Color(0xFF16A34A), Color(0xFF4ADE80)),
            modifier = Modifier.widthIn(min = 220.dp).weight(1f),
        )
        GradientStatCard(
            title = "Домашние задания",
            value = "${stats.homeworkAveragePercent.roundToInt()}%",
            subtitle = "${stats.homeworkCompleted}/${stats.homeworkTotal} сдано",
            colors = listOf(Color(0xFF2563EB), Color(0xFF60A5FA)),
            modifier = Modifier.widthIn(min = 220.dp).weight(1f),
        )
    }

    Text(
        "Занятия",
        color = colors.textPrimary,
        fontWeight = FontWeight.Bold,
        fontSize = 18.sp,
        modifier = Modifier.padding(top = 22.dp, bottom = 4.dp),
    )
    MutedText(
        "От новых к старым · тема и связанные ДЗ",
        modifier = Modifier.padding(bottom = 12.dp),
    )

    if (groups.isEmpty()) {
        MutedText("Занятий и домашних заданий пока нет.")
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            groups.forEach { group ->
                SessionGroupCard(
                    group = group,
                    showSubmissionLinks = showSubmissionLinks,
                    onOpenHomework = onOpenHomework,
                )
            }
        }
    }
}

private fun buildSessionGroups(
    attendanceItems: List<AttendanceSessionItem>,
    homeworkScores: List<HomeworkScoreItem>,
): List<SessionStatsGroup> {
    val sessionsNewestFirst = attendanceItems.sortedWith(
        compareByDescending<AttendanceSessionItem> { it.date }
            .thenByDescending { it.title }
            .thenByDescending { it.sessionId },
    )
    val remaining = homeworkScores.toMutableList()
    val groups = sessionsNewestFirst.map { session ->
        val linked = remaining.filter { hw ->
            hw.sessionId == session.sessionId ||
                (
                    hw.sessionId.isNullOrBlank() &&
                        hw.sessionDate == session.date &&
                        hw.sessionTitle == session.title
                    )
        }
        remaining.removeAll(linked.toSet())
        SessionStatsGroup(
            session = session,
            homeworks = linked.sortedBy { it.title },
        )
    }.toMutableList()

    if (remaining.isNotEmpty()) {
        val byLooseKey = remaining.groupBy { hw ->
            "${hw.sessionDate}\u0000${hw.sessionTitle}"
        }
        byLooseKey.entries
            .sortedByDescending { (key, _) -> key }
            .forEach { (key, items) ->
                val date = key.substringBefore('\u0000')
                val title = key.substringAfter('\u0000')
                if (date.isBlank() && title.isBlank()) {
                    groups.add(SessionStatsGroup(session = null, homeworks = items.sortedBy { it.title }))
                } else {
                    groups.add(
                        SessionStatsGroup(
                            session = AttendanceSessionItem(
                                sessionId = items.first().sessionId.orEmpty(),
                                date = date,
                                title = title.ifBlank { "Занятие" },
                                status = null,
                            ),
                            homeworks = items.sortedBy { it.title },
                        ),
                    )
                }
            }
    }
    return groups
}


@Composable
private fun SessionGroupCard(
    group: SessionStatsGroup,
    showSubmissionLinks: Boolean,
    onOpenHomework: ((HomeworkScoreItem) -> Unit)?,
) {
    val colors = GradeTheme.colors
    val shape = RoundedCornerShape(14.dp)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(colors.card, shape)
            .border(1.dp, colors.border, shape)
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        val session = group.session
        if (session == null) {
            Text(
                "ДЗ без занятия",
                color = YellowAccent,
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
            )
            MutedText("Старые или непривязанные задания")
        } else {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top,
            ) {
                Column(modifier = Modifier.weight(1f).padding(end = 12.dp)) {
                    Text(
                        "ТЕМА",
                        color = SessionAccent,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 0.5.sp,
                    )
                    Text(
                        session.title.ifBlank { "Занятие" },
                        color = colors.textPrimary,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 16.sp,
                        modifier = Modifier.padding(top = 2.dp),
                    )
                    if (session.date.isNotBlank()) {
                        MutedText(formatDisplayDate(session.date), modifier = Modifier.padding(top = 2.dp))
                    }
                }
                Column(horizontalAlignment = Alignment.End) {
                    Text(
                        "ПОСЕЩЕНИЕ",
                        color = colors.textMuted,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 0.4.sp,
                    )
                    Text(
                        session.status?.let { statusFullLabel(it) } ?: "Нет отметки",
                        color = statusColor(session.status),
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
            }
        }

        if (group.homeworks.isEmpty()) {
            MutedText("К этой теме ДЗ пока нет.")
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                group.homeworks.forEach { item ->
                    HomeworkScoreRow(
                        item = item,
                        compact = true,
                        showSubmissionLinks = showSubmissionLinks,
                        onOpen = onOpenHomework?.let { open -> { open(item) } },
                    )
                }
            }
        }
    }
}

@Composable
private fun HomeworkScoreRow(
    item: HomeworkScoreItem,
    compact: Boolean = false,
    showSubmissionLinks: Boolean = false,
    onOpen: (() -> Unit)?,
) {
    val colors = GradeTheme.colors
    val shape = RoundedCornerShape(10.dp)
    val bg = if (compact) colors.nestedCard else colors.card
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(bg, shape)
            .border(1.dp, colors.border.copy(alpha = if (compact) 0.7f else 1f), shape)
            .then(
                if (onOpen != null) {
                    Modifier.softClickable(
                        onClick = onOpen,
                        highlight = YellowAccent,
                        shape = shape,
                        hoverAlpha = 0.1f,
                        pressAlpha = 0.18f,
                    )
                } else {
                    Modifier
                },
            )
            .padding(horizontal = 12.dp, vertical = 10.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(
                modifier = Modifier.weight(1f).padding(end = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    "ДЗ",
                    color = YellowAccent,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                )
                Text(
                    item.title,
                    color = colors.textPrimary,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 14.sp,
                )
            }
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    item.score?.let { trimScore(it) } ?: "—",
                    color = if (item.score == null) colors.textMuted else GreenAccent,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 14.sp,
                )
                if (onOpen != null) {
                    ChevronRight(color = colors.textMuted)
                }
            }
        }
        Spacer(Modifier.height(8.dp))
        LinearProgressIndicator(
            progress = { ((item.percent ?: 0.0) / 100.0).toFloat().coerceIn(0f, 1f) },
            modifier = Modifier.fillMaxWidth().height(4.dp),
            color = BlueAccent,
            trackColor = colors.border,
        )
        if (showSubmissionLinks && item.submissionUrls.isNotEmpty()) {
            Spacer(Modifier.height(8.dp))
            MutedText(
                "Ответы (${item.submissionUrls.size})",
                modifier = Modifier.padding(bottom = 4.dp),
            )
            item.submissionUrls.forEach { url ->
                val linkShape = RoundedCornerShape(10.dp)
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 6.dp)
                        .background(colors.surface, linkShape)
                        .border(1.dp, colors.border.copy(alpha = 0.8f), linkShape)
                        .clip(linkShape)
                        .padding(horizontal = 10.dp, vertical = 8.dp),
                ) {
                    LinkedText(
                        text = url,
                        color = BlueAccent,
                        fontSize = 12.sp,
                    )
                }
            }
        }
    }
}

@Composable
private fun ChevronRight(color: Color) {
    Box(
        modifier = Modifier.size(16.dp),
        contentAlignment = Alignment.Center,
    ) {
        Canvas(modifier = Modifier.size(12.dp)) {
            val stroke = 2.dp.toPx()
            val w = size.width
            val h = size.height
            drawLine(
                color = color,
                start = Offset(w * 0.3f, h * 0.18f),
                end = Offset(w * 0.72f, h * 0.5f),
                strokeWidth = stroke,
                cap = StrokeCap.Round,
            )
            drawLine(
                color = color,
                start = Offset(w * 0.72f, h * 0.5f),
                end = Offset(w * 0.3f, h * 0.82f),
                strokeWidth = stroke,
                cap = StrokeCap.Round,
            )
        }
    }
}
